<template>
  <section class="samsung-stage">
    <div class="samsung-stage__background">
      <GridContainer
        padding
        class="samsung-stage__content-wrapper"
      >
        <div class="samsung-stage__logo">
          <SvgIcon
            class="samsung-stage__logo-icon"
            :image="SamsungLogo"
          />
        </div>
        <Heading
          tag="h1"
          font-weight="ultra"
          size="xl"
          text-uppercase
          align="center"
          class="samsung-stage__heading"
        >
          {{ data.mainTitle }}
        </Heading>
        <Paragraph
          is-size-big
          align="center"
          class="samsung-stage__description"
        >
          {{ data.description }}
        </Paragraph>
        <ul
          v-if="data.products"
          class="samsung-stage__list"
        >
          <li
            v-for="(item, index) in data.products"
            :key="`product-${index}`"
            :ref="item.ref"
            class="samsung-stage__product"
            :class="`samsung-stage__product--${item.ref}`"
          >
            <CdnImage
              class="samsung-stage__image"
              :src="item.src"
              :alt="item.alt"
              :style="`--aspect-ratio: ${item.aspectRatio}`"
              lazy
              sizes="xs:60vw sm:50vw md:50vw lg:50vw"
            />
            <PlusIconOpener
              v-if="item.plusIcon"
              class="samsung-stage__plus-icon"
              :class="`samsung-stage__plus-icon--${item.ref}`"
              :title="item.plusIcon.title"
              :label="item.plusIcon.label"
              :aria-label="item.plusIcon.ariaLabel"
              :xs-open-left="item.plusIcon.xsOpenLeft"
              :sm-open-left="item.plusIcon.smOpenLeft"
              :md-open-left="item.plusIcon.mdOpenLeft"
              :anker-link="item.ref"
              @go-to-section="emitGoToSection"
            />
          </li>
        </ul>
      </GridContainer>
    </div>
  </section>
</template>

<script>
import SamsungLogo from '@/assets/images/samsung/samsung-logo.svg';
import { GridContainer, Heading, Paragraph } from '@i22-td-smarthome/component-library';
import ScrollAnimation from '@i22/scroll-animation';
import PlusIconOpener from '@/components/plus-icon-opener.vue';
import CdnImage from '@/components/cdn-image.vue';

export default {
  name: 'SamsungStage',
  components: { GridContainer, Heading, PlusIconOpener, Paragraph, CdnImage },
  props: {
    data: {
      type: Object,
      required: true,
    },
  },
  mixins: [ScrollAnimation],
  emits: ['go-to-section'],
  data() {
    return {
      SamsungLogo,
    };
  },
  mounted() {
    this.initAnimations();
  },
  methods: {
    emitGoToSection(anker) {
      this.$emit('go-to-section', anker);
    },
    initAnimations() {
      const refsCollection = this.data.products.map((item) => ({
        trigger: this.$refs[item.ref],
        ref: item.ref,
        positionY: item.positionY,
      }));

      refsCollection.forEach((item, index) => {
        this.createMediaScrollTrigger({
          '(max-width: 639px)': () => {
            this.createScrollTrigger({
              trigger: item.trigger,
              start: 'top bottom',
              end: 'bottom top',
              scrub: index,
              ease: 'expo.inOut',
              markers: false,
              animation: this.createTweenFrom(`.samsung-stage__product--${item.ref}`, {
                yPercent: item.positionY.mobile,
              }),
            });
          },
          '(min-width: 640px)': () => {
            this.createScrollTrigger({
              trigger: item.trigger,
              start: 'top bottom',
              end: 'bottom top',
              scrub: index + 2,
              ease: 'expo.inOut',
              markers: false,
              animation: this.createTweenFrom(`.samsung-stage__product--${item.ref}`, {
                yPercent: item.positionY.desktop,
              }),
            });
          },
        });
      });
    },
  },
};
</script>

<style lang="scss" scoped>
@use 'assets/base' as *;

.samsung-stage {
  overflow: hidden;
  @include mq($mq-md) {
    padding-bottom: $grid-base * 3;
  }
  &__background {
    padding-top: $grid-base * 2;
    padding-bottom: 8 * $grid-base;
    background-color: color(grey, 33000);
    margin-bottom: 150px;
    @include mq($mq-sm) {
      margin-bottom: 150px;
      min-height: 800px;
      padding-bottom: 0;
    }
    @include mq($mq-md) {
      padding-top: $grid-base * 4;
      min-height: 680px;
      margin-bottom: 200px;
    }
  }

  &__heading {
    max-width: 920px;
    margin: 0 auto;
    padding-bottom: 0.5em;
    padding-top: 0.5em;

    @include mq($mq-md) {
      margin: 0;
      text-align: left;
    }
  }

  &__description {
    max-width: 980px;
    margin: 0 auto;

    @include mq($mq-md) {
      margin: 0;
      text-align: left;
      max-width: 40%;
    }
  }

  &__list {
    margin: 0 auto;
    @include mq(false, $mq-sm - 1) {
      display: flex;
      flex-wrap: wrap;
      flex-direction: column;
      align-content: space-between;
      max-width: 500px;
    }
    @include mq($mq-sm) {
      position: relative;
      top: $grid-base;
      max-width: 800px;
    }
    @include mq($mq-md) {
      position: absolute;
      width: 70%;
      right: 0;
      top: 35%;
    }
  }

  &__content-wrapper {
    position: relative;
  }

  &__logo {
    text-align: right;
  }

  &__logo-icon {
    height: 18px;
    width: auto;
    @include mq($mq-sm) {
      height: 24px;
    }
  }

  &__image {
    position: relative;
  }
  // Position Product List Item
  &__product {
    position: relative;
    list-style: none;
    @include mq(false, $mq-sm - 1) {
      width: 100%;
      display: flex;
      &--galaxyWatch7 {
        justify-content: flex-start;
        :deep(.samsung-stage__image) {
          width: 60%;
          max-width: 250px;
          img {
            aspect-ratio: var(--aspect-ratio, 1);
            width: 100%;
          }
        }
      }
      &--samsungS25 {
        justify-content: flex-end;
        margin-top: -125px;
        :deep(.samsung-stage__image) {
          max-width: 172px;
          width: 43%;
          img {
            aspect-ratio: var(--aspect-ratio, 1);
            width: 100%;
          }
        }
      }
      &--galaxyBuds3 {
        margin-top: 0px;
        :deep(.samsung-stage__image) {
          width: 58%;
          max-width: 250px;
          img {
            aspect-ratio: var(--aspect-ratio, 1);
            width: 100%;
          }
        }
      }
      &--samsungS25Plus {
        margin-top: -30px;
        margin-left: 12px;
        :deep(.samsung-stage__image) {
          width: 40%;
          max-width: 175px;
          img {
            aspect-ratio: var(--aspect-ratio, 1);
            width: 100%;
          }
        }
      }
      &--samsungS25Ultra {
        margin-top: 20px;
        justify-content: flex-end;
        :deep(.samsung-stage__image) {
          width: 55%;
          aspect-ratio: var(--aspect-ratio, 1);
          max-width: 200px;
          img {
            width: 100%;
            aspect-ratio: var(--aspect-ratio, 1);
          }
        }
      }
    }
    @include mq($mq-sm) {
      position: absolute;
      top: 0;
      &--galaxyWatch7 {
        left: -6%;
        z-index: 3;
        width: 35%;
        margin-top: 40%;
      }
      &--samsungS25 {
        left: 18%;
        z-index: 2;
        width: 25%;
        margin-top: 18%;
      }
      &--galaxyBuds3 {
        left: unset;
        right: -6%;
        z-index: 3;
        width: 35%;
        margin-top: 35%;
      }
      &--samsungS25Plus {
        left: 38%;
        width: 25%;
        z-index: 1;
        margin-top: 14%;
      }
      &--samsungS25Ultra {
        right: 8%;
        width: 35%;
        margin-top: 7%;
      }
    }
    @include mq($mq-md) {
      &--galaxyWatch7 {
        width: 37%;
      }
      &--samsungS25 {
        left: 20%;
        width: 26%;
      }
      &--galaxyBuds3 {
        margin-top: 40%;
      }
      &--samsungS25Plus {
        width: 25%;
        left: 38%;
        margin-top: 16%;
      }
      &--samsungS25Ultra {
        margin-top: 10%;
      }
    }

    @include mq($mq-lg) {
      &--galaxyWatch7 {
        left: -18%;
      }
      &--samsungS25 {
        left: 8%;
      }
      &--samsungS25Plus {
        left: 28%;
      }
      &--samsungS25Ultra {
        right: 16%;
      }
      &--galaxyBuds3 {
        right: 0;
      }
    }
  }
  // Position Icon
  &__plus-icon {
    z-index: 3;
    &--galaxyWatch7 {
      top: 0;
      left: 30%;
      @include mq($mq-sm) {
        left: 15%;
        right: auto;
      }
    }
    &--samsungS25 {
      transform: translateY(0);
      top: 20%;
      right: 0;
      @include mq($mq-sm) {
        top: 0;
        right: calc(100% - 43px);
      }
    }
    &--galaxyBuds3 {
      top: 55%;
      left: 32%;
      @include mq($mq-sm) {
        top: 55%;
        left: 0;
      }
      @include mq($mq-md) {
        top: 60%;
        left: 5%;
      }
    }
    &--samsungS25Plus {
      top: 50%;
      left: 28%;

      @include mq($mq-sm) {
        top: 5%;
        left: unset;
        right: -5%;
      }
    }
    &--samsungS25Ultra {
      top: 15%;
      right: 35%;

      @include mq($mq-sm) {
        top: -2 * $grid-base;
        right: 15%;
      }
    }
  }
}
</style>
